import React, { type FC, type ReactNode, useMemo, useRef, useState } from 'react'
import { buttonClasses, Menu, type SxProps, type Theme } from '@mui/material'

import { isArray } from '../../../../utils'

import { extractDataTestId } from '../../utils'
import { ChevronDownIcon } from '../Branding'
import { Button } from '../Button'
import { MenuItem } from './MenuItem'
import { type DropdownSize, MenuItemClasses } from './utils'

type DropdownValue = string | number

export type DropdownOptionProps<T extends DropdownValue> = Readonly<{
    value: T
    label: ReactNode
}>

type DropdownProps<T extends DropdownValue> = Readonly<{
    options: readonly DropdownOptionProps<T>[]
    size: DropdownSize
    onChange: (value: T) => void
    value: T | null | readonly T[]
    buttonLabel: ReactNode
}>

const createIconStyle = (rotation: number): SxProps<Theme> => ({
    [`& .${buttonClasses.endIcon}`]: { transform: `rotate(${rotation}deg)`, transition: 'transform 200ms ease' },
})

const closedIconStyle = createIconStyle(180)
const openIconStyle = createIconStyle(0)

export const Dropdown = <T extends DropdownValue>({ buttonLabel, options, size, value, onChange, ...props }: DropdownProps<T>): ReturnType<FC> => {
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [opened, setOpened] = useState<boolean>(false)

    const selectedValues = useMemo<readonly T[]>(() => {
        if (value === null) {
            return []
        }
        return isArray(value) ? value : [value]
    }, [value])

    const close = (): void => {
        setOpened(false)
    }

    const isSelected = (optionValue: T): boolean => selectedValues.includes(optionValue)

    const selectOption = (selectedValue: T): void => {
        onChange(selectedValue)
        close()
    }

    return (
        <>
            <Button
                ref={buttonRef}
                data-testid={extractDataTestId(props, '-button')}
                variant="secondary"
                size={size}
                endIcon={<ChevronDownIcon />}
                sx={opened ? openIconStyle : closedIconStyle}
                aria-expanded={opened}
                onClick={() => setOpened(true)}
            >
                {buttonLabel}
            </Button>

            <Menu data-testid={extractDataTestId(props, '-menu')} anchorEl={buttonRef.current} open={opened} onClose={close}>
                {options.map((option) => (
                    <MenuItem
                        sizeClassName={MenuItemClasses[size]}
                        data-testid={extractDataTestId(props, `-item-${option.value}`)}
                        key={option.value}
                        selected={isSelected(option.value)}
                        onClick={() => selectOption(option.value)}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}
